<template>
    <div
        class="betBuilderMultipleBetType notranslate"
        v-bind:class="[styleWidth, { live: event.isLive }]"
    >
        <strong class="betBuilderMultipleBetType_title">
            <div
                v-if="isShowScore && liveScore"
                class="score"
                data-testid="bet-builder-group-score"
            >
                [ {{ liveScore.home }}<span>:</span>{{ liveScore.away }} ]
            </div>
            {{ title }}
        </strong>
        <div class="betBuilderMultipleBetType_content">
            <div class="betBuilderMultipleBetType_col betBuilderMultipleBetType_col-header">
                <strong class="betBuilderMultipleBetType_colTitle">
                    {{ i18n.t('odds_option_event') }}
                </strong>
                <template
                    v-for="block in marketBlocks"
                    v-bind:key="block.key"
                >
                    <strong
                        v-for="(label, labelIndex) in group.rowLabels"
                        v-bind:key="`${block.key}-${label}`"
                        class="oddsItem"
                        v-bind:class="{ 'team-favorite': labelIndex === block.favoriteRowIndex }"
                    >
                        {{ label }}
                    </strong>
                    <strong v-if="block.has1X2" class="oddsItem">
                        {{ i18n.t('odds_option_draw') }}
                    </strong>
                </template>
            </div>
            <div
                v-for="column in renderColumns"
                v-bind:key="column.key"
                class="betBuilderMultipleBetType_col"
            >
                <strong class="betBuilderMultipleBetType_colTitle">
                    {{ column.label }}
                </strong>
                <template
                    v-for="cell in column.cells"
                    v-bind:key="cell.key"
                >
                    <div
                        v-if="cell.entry"
                        v-tooltip:top="column.rule.tooltipFormatter(cell.entry)"
                        class="oddsItem"
                    >
                        <Selection
                            v-bind:matchId="event.id"
                            v-bind:market="cell.entry.market"
                            v-bind:selection="cell.entry.selection"
                            presentation="horizontal"
                            class="oddsPrice"
                        />
                    </div>
                    <div v-else class="oddsItem" />
                </template>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, toRefs } from 'vue';
    import { getMarketRule } from '../betBuilderMarketRules';
    import Selection from '@/components/odds/Selection.vue';
    import { getHandicapSide } from '@/core/lib/betBuilderHelper';
    import { betBuilderMultipleGroupToBetTypeGroup } from '@/core/lib/betBuilderTypeConverter';
    import { i18n } from '@/core/lib/i18n';
    import { isBetBuilder1X2 } from '@/core/lib/oddsHelper';
    import { getTranslated } from '@/core/lib/translationHelper';
    import { getScore, isShowScoreInMoreMarket } from '@/core/oddsApi/helpers/liveScore';
    import type { IEvent } from '@/interface/IEvent';
    import type { IBetBuilderMultipleBetTypeGroup } from '@/interface/betBuilder';
    import { BetBuilderDisplayType } from '@/interface/enum';

    const props = defineProps<{
        event: IEvent;
        group: IBetBuilderMultipleBetTypeGroup;
    }>();

    const { event } = toRefs(props);

    const marketBlocks = computed(() => {
        const blockCount = Math.max(1, ...props.group.columns.map(column => column.markets.length));

        return Array.from({ length: blockCount }, (_, marketIndex) => {
            const blockMarkets = props.group.columns
                .map(column => column.markets[marketIndex])
                .filter(market => market !== undefined);
            const has1X2 = blockMarkets.some(market => isBetBuilder1X2(market.displayType));

            return {
                key: blockMarkets.map(market => market.matchMarketId).join('-'),
                marketIndex,
                has1X2,
                favoriteRowIndex: getFavoriteRowIndex(blockMarkets),
                rowCount: props.group.rowLabels.length + (has1X2 ? 1 : 0),
            };
        });
    });

    const renderColumns = computed(() => props.group.columns.map((column) => {
        const rule = getMarketRule(column.markets[0]);
        const cells = marketBlocks.value.flatMap((block) => {
            const market = column.markets[block.marketIndex];
            const marketCells = market === undefined
                ? []
                : rule.selectionFormatter(market.selections).map(selection => ({
                    key: `${market.matchMarketId}-${selection.type.id}`,
                    entry: 'id' in selection ? { market, selection } : null,
                }));

            return Array.from({ length: block.rowCount }, (_, rowIndex) => marketCells[rowIndex]
                ?? { key: `${column.key}-${block.key}-pad-${rowIndex}`, entry: null });
        });

        return {
            key: column.key,
            label: column.label,
            rule,
            cells,
        };
    }));

    const styleWidth = computed(() => {
        if (props.group.columns.length === 1) return 'width-31';
        if (props.group.columns.length === 2 || props.group.columns.length === 3) return 'width-32';
        return 'width-full';
    });

    const betTypeGroup = computed(() => betBuilderMultipleGroupToBetTypeGroup(props.group.type));

    const title = computed(() => {
        const eventResult = event.value.eventResults.find(result => result.marketGroup.id === betTypeGroup.value);
        const name = getTranslated(eventResult?.marketGroup.marketGroupName) || props.group.title;
        const prefixKey = props.group.titlePrefixKey;

        return prefixKey === '' ? name : `${i18n.t(prefixKey)} ${name}`;
    });

    const isShowScore = computed(() => isShowScoreInMoreMarket(event, betTypeGroup.value));

    const liveScore = computed(() => getScore(event.value.eventResults, betTypeGroup.value));

    function getFavoriteRowIndex(markets: IBetBuilderMultipleBetTypeGroup['columns'][number]['markets']): number {
        const handicap = markets.find(market => market.displayType === BetBuilderDisplayType.Handicap
            || market.displayType === BetBuilderDisplayType.FH_Handicap);
        if (handicap === undefined || handicap.point === 0) return -1;

        const handicapSide = getHandicapSide(handicap);
        return getMarketRule(handicap).selectionFormatter(handicap.selections)
            .findIndex(selection => selection.type.id === handicapSide);
    }
</script>

<style lang="scss" scoped>
    @import "@/components/oddsDisplay/moreMarket/marketType/BetBuilderMultipleBetType";
</style>
